import React, { createContext, useState, useEffect, useMemo, ReactNode } from 'react';

export interface CartItem {
  product_id: string;
  packing_size: string;
  num_cases: number;
  product_name?: string;
  unit_price?: number;
  image?: string;
  [key: string]: unknown;
}

export interface CartContextValue {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: string, packingSize: string) => void;
  updateQuantity: (productId: string, packingSize: string, newQty: number) => void;
  clearCart: () => void;
  getCartCount: () => number;
  getItemQty: (productId: string, packingSize?: string) => number;
  decrementItem: (productId: string, packingSize?: string) => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('cart');
      return saved ? (JSON.parse(saved) as CartItem[]) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item: CartItem) => {
    setCartItems((prev) => {
      const existing = prev.findIndex((i) => i.product_id === item.product_id && i.packing_size === item.packing_size);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], num_cases: updated[existing].num_cases + item.num_cases };
        return updated;
      }
      return [...prev, item];
    });
  };

  const removeFromCart = (productId: string, packingSize: string) => {
    setCartItems((prev) => prev.filter((i) => !(i.product_id === productId && i.packing_size === packingSize)));
  };

  const updateQuantity = (productId: string, packingSize: string, newQty: number) => {
    if (newQty < 1) return;
    setCartItems((prev) => prev.map((i) =>
      i.product_id === productId && i.packing_size === packingSize
        ? { ...i, num_cases: newQty }
        : i
    ));
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cart');
  };

  const getCartCount = (): number => cartItems.length;

  const getItemQty = (productId: string, packingSize?: string): number => {
    const item = cartItems.find((i) => i.product_id === productId && (!packingSize || i.packing_size === packingSize));
    return item ? item.num_cases : 0;
  };

  const decrementItem = (productId: string, packingSize?: string) => {
    const item = cartItems.find((i) => i.product_id === productId && (!packingSize || i.packing_size === packingSize));
    if (!item) return;
    if (item.num_cases <= 1) {
      removeFromCart(productId, item.packing_size);
    } else {
      updateQuantity(productId, item.packing_size, item.num_cases - 1);
    }
  };

  // Memoize the context value so consumers don't re-render every time this
  // provider re-renders. Safe because all actions capture `setCartItems`
  // which is stable.
  const value = useMemo<CartContextValue>(() => ({
    cartItems, addToCart, removeFromCart, updateQuantity, clearCart,
    getCartCount, getItemQty, decrementItem,
  }), [cartItems]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
